import React, { useEffect, useRef, useState } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_IMAGE_CLEAR_BUTTON_SX as IMAGE_CLEAR_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX } from '../../component/formSurfaceStyles'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'
import {
    DONATION_IMAGE_FILE_ACCEPT,
    DONATION_IMAGE_FILE_ERROR_MESSAGE,
    applyDonationDraft,
    clearDonationDraft,
    createDonationDraftPayload,
    createLegacyDonationDraftPayload,
    isDonationImageFile,
    NEW_DONATION_DRAFT_KEY,
    readDonationDraft,
    saveDonationDraft,
} from './donationDraftUtils'
import {
    DONATION_IMAGE_LEAVE_MESSAGE,
    hasPendingDonationImages,
    usePendingDonationImageUnloadGuard,
} from './donationLeaveGuard'

const DONATION_SELECT_PARAMS = {
    searchStatus: 'searchStatus',
}
const DONATION_DRAFT_FIELDS = [
    { field: 'title', param: 'draftTitle' },
    { field: 'brandName', param: 'draftBrandName' },
    { field: 'description', param: 'draftDescription' },
    { field: 'point', param: 'draftPoint' },
    { field: 'sortOrder', param: 'draftSortOrder' },
    { field: 'status', param: 'draftStatus' },
]

const createPreviewState = () => ({
    productImage: '/images/noimage.jpg',
    letterBaseImage: '/images/noimage.jpg',
})

const IMAGE_PANEL_SX = {
    ...FORM_PANEL_SX,
    height: '100%',
}

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

// 기부 상품 이미지를 미리보기, 업로드, 선택 취소까지 한 패널 안에서 관리한다.
function DonationImageUploadPanel({
    fieldName,
    label,
    helperText,
    imageSrc,
    imageFile,
    disabled,
    onUpload,
    onClear,
}) {
    const imageFileName = getImageFileName(imageFile)
    const hasPendingFile = imageFile instanceof File
    const fileInputRef = useRef(null)

    const handleClearClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }

        onClear(fieldName)
    }

    return (
        <Paper variant="outlined" sx={IMAGE_PANEL_SX}>
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {helperText}
                    </Typography>
                </Box>
                {hasPendingFile && (
                    <Button
                        size="small"
                        color="error"
                        variant="text"
                        startIcon={<RemoveIcon />}
                        onClick={handleClearClick}
                        sx={IMAGE_CLEAR_BUTTON_SX}
                        disabled={disabled}
                    >
                        선택 취소
                    </Button>
                )}
            </Box>
            <Box
                sx={{
                    ...FORM_IMAGE_PREVIEW_FRAME_SX,
                    height: 240,
                    mb: 1.5,
                }}
            >
                <Box
                    component="img"
                    src={imageSrc || '/images/noimage.jpg'}
                    alt={`${label} 미리보기`}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                    }}
                />
            </Box>
            <FormControl>
                <TextField
                    inputProps={{ accept: DONATION_IMAGE_FILE_ACCEPT }}
                    inputRef={fileInputRef}
                    style={{ display: "none" }}
                    name={fieldName}
                    id={fieldName}
                    type="file"
                    disabled={disabled}
                    onChange={onUpload(fieldName)}
                />
                <label htmlFor={fieldName}>
                    <Button
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        이미지 선택
                    </Button>
                </label>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

export default function NewDonation() {
    const history = useHistory()
    const location = useLocation()
    const initialPageStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: { searchStatus: '' },
        selectParams: DONATION_SELECT_PARAMS,
        getExtraState: ({ locationState, searchParams }) => ({
            draftValues: DONATION_DRAFT_FIELDS.reduce((nextDrafts, { field, param }) => {
                nextDrafts[field] = searchParams.has(param)
                    ? (searchParams.get(param) || '')
                    : (locationState[field] ?? null)
                return nextDrafts
            }, {}),
        }),
    }))
    const initialPageState = initialPageStateRef.current
    const legacyDraftRef = useRef(createLegacyDonationDraftPayload(initialPageState.draftValues))
    const listCurPage = initialPageState.curPage
    const listSearchText = initialPageState.searchText
    const listSearchSelect = initialPageState.searchSelect
    const draftKey = NEW_DONATION_DRAFT_KEY
    const storedDraftRef = useRef(readDonationDraft(draftKey))
    const listSearch = buildListSearchQuery({
        curPage: listCurPage,
        searchQuery: listSearchText,
        searchSelect: listSearchSelect,
        selectParams: DONATION_SELECT_PARAMS,
    })
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState({
        title: '',
        brandName: '',
        description: '',
        point: '',
        sortOrder: 0,
        status: 1,
        productImage: null,
        letterBaseImage: null,
    })
    const [previewImages, setPreviewImages] = useState(createPreviewState())
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const leaveGuardEnabled = hasPendingDonationImages(params) && !saving && !navigationUnlocked

    usePendingDonationImageUnloadGuard(leaveGuardEnabled)

    useEffect(() => {
        const baseParams = {
            title: '',
            brandName: '',
            description: '',
            point: '',
            sortOrder: 0,
            status: 1,
        }
        const restoredDraft = applyDonationDraft({
            baseParams,
            draft: storedDraftRef.current || legacyDraftRef.current,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createDonationDraftPayload({
            params: baseParams,
        }))

        setParams((prev) => ({
            ...prev,
            ...restoredDraft.params,
        }))
        setDraftReady(true)
    }, [])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    useEffect(() => {
        if (location.search === listSearch) {
            return
        }

        history.replace({
            pathname: location.pathname,
            search: listSearch,
            ...(location.state ? { state: location.state } : {}),
        })
    }, [history, listSearch, location.pathname, location.search, location.state])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // 포인트 기부 상품 등록은 목록 query를 건드리지 않고 텍스트 draft만 sessionStorage에 보존한다.
        const nextDraft = createDonationDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearDonationDraft(draftKey)
            return
        }

        saveDonationDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, params])

    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        clearDonationDraft(draftKey)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 query는 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: '/donations',
                search: listSearch,
                curPage: listCurPage,
                searchText: listSearchText,
                searchSelect: listSearchSelect,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {}),
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (!leaveGuardEnabled) {
            clearDonationDraft(draftKey)
            return
        }

        event.preventDefault()

        if (!window.confirm(DONATION_IMAGE_LEAVE_MESSAGE)) {
            return
        }

        moveToList()
    }

    const handleUpdateField = (e) => {
        setParams((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const handleUploadClick = (fieldName) => (e) => {
        e.preventDefault()

        const file = e.target.files?.[0]
        if (!file) {
            return
        }

        if (!isDonationImageFile(file)) {
            e.target.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(DONATION_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        const reader = new FileReader()

        reader.onloadend = () => {
            setParams((prev) => ({
                ...prev,
                [fieldName]: file
            }))
            setPreviewImages((prev) => ({
                ...prev,
                [fieldName]: reader.result
            }))
        }

        reader.readAsDataURL(file)
    }

    const handleClearSelectedImage = (fieldName) => {
        setParams((prev) => ({
            ...prev,
            [fieldName]: null,
        }))
        setPreviewImages((prev) => ({
            ...prev,
            [fieldName]: createPreviewState()[fieldName],
        }))
    }

    const createData = async (formData) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/donation/create',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' }
            })

            if (data.code === 200) {
                moveToList({
                    severity: 'success',
                    message: '포인트 기부 상품을 등록했습니다.',
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('포인트 기부 상품을 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const trimmedTitle = (params.title || '').trim()
        const trimmedBrandName = (params.brandName || '').trim()
        const normalizedPoint = Number(params.point)

        if (!trimmedTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('상품명을 입력해주세요.')
            return
        }

        if (!trimmedBrandName) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('브랜드명을 입력해주세요.')
            return
        }

        if (!Number.isFinite(normalizedPoint) || normalizedPoint < 1) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('포인트는 1 이상으로 입력해주세요.')
            return
        }

        if (!(params.productImage instanceof File)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('상품 이미지를 업로드해주세요.')
            return
        }

        if (!(params.letterBaseImage instanceof File)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('감사 편지 기본 이미지를 업로드해주세요.')
            return
        }

        const formData = new FormData()
        formData.append('title', trimmedTitle)
        formData.append('brandName', trimmedBrandName)
        formData.append('description', (params.description || '').trim())
        formData.append('point', normalizedPoint)
        formData.append('sortOrder', Number(params.sortOrder || 0))
        formData.append('status', Number(params.status))

        formData.append('productImage', params.productImage)
        formData.append('letterBaseImage', params.letterBaseImage)

        createData(formData)
    }

    const renderImageField = (fieldName, label, helperText) => (
        <Grid item xs={12} md={4}>
            <DonationImageUploadPanel
                fieldName={fieldName}
                label={label}
                helperText={helperText}
                imageSrc={previewImages[fieldName]}
                imageFile={params[fieldName]}
                disabled={saving}
                onUpload={handleUploadClick}
                onClear={handleClearSelectedImage}
            />
        </Grid>
    )

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Prompt when={leaveGuardEnabled} message={DONATION_IMAGE_LEAVE_MESSAGE} />
                <Title>포인트 기부 상품 등록</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="상품명"
                                    name="title"
                                    value={params.title}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="브랜드명"
                                    name="brandName"
                                    value={params.brandName}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    type="number"
                                    label="포인트"
                                    name="point"
                                    value={params.point}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    type="number"
                                    label="정렬순서"
                                    name="sortOrder"
                                    value={params.sortOrder}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    select
                                    label="상태"
                                    name="status"
                                    value={params.status}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                >
                                    <MenuItem value={1}>활성화</MenuItem>
                                    <MenuItem value={0}>비활성화</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="설명"
                                    name="description"
                                    value={params.description}
                                    onChange={handleUpdateField}
                                    size="small"
                                    multiline
                                    minRows={4}
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>

                        {renderImageField('productImage', '상품 이미지', '원본 이미지 저장 후 서버에서 썸네일을 자동 생성합니다.')}
                        {renderImageField('letterBaseImage', '감사 편지 기본 이미지', '앱에서 문구와 날짜를 오버레이하는 기본 배경 이미지입니다.')}
                    </Grid>

                    <BottomButtonGroup
                        path="/donations"
                        search={listSearch}
                        curPage={listCurPage}
                        searchText={listSearchText}
                        searchSelect={listSearchSelect}
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        onBackClick={handleBackClick}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
